import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Storefront, MagnifyingGlass, MapPin, Globe, Phone, CheckCircle, Lock } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const VendorDirectory = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [county, setCounty] = useState("");
  const [categories, setCategories] = useState([]);
  const [counties, setCounties] = useState([]);
  const [total, setTotal] = useState(0);

  const isPremium = user?.subscription_tier === "pro" || user?.role === "admin";

  useEffect(() => {
    if (isPremium) fetchVendors();
    else setLoading(false);
  }, [category, county]); // eslint-disable-line

  const fetchVendors = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (category) params.append("category", category);
      if (county) params.append("county", county);
      params.append("limit", "50");

      const res = await axios.get(`${API}/vendors?${params}`);
      setVendors(res.data.vendors);
      setTotal(res.data.total);
      setCategories(res.data.categories || []);
      setCounties(res.data.counties || []);
    } catch (error) {
      toast.error("Failed to load vendors");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchVendors();
  };

  if (!isPremium) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4" data-testid="vendor-locked">
          <div className="w-16 h-16 rounded-full bg-zinc-900 flex items-center justify-center mb-6">
            <Lock weight="duotone" className="w-8 h-8 text-zinc-500" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Vendor Directory</h2>
          <p className="text-zinc-400 mb-6 max-w-md">
            Access our curated directory of SC vendors and service providers trusted by nonprofits. Know exactly where to spend your grant funds.
          </p>
          <Button
            onClick={() => navigate("/pricing")}
            className="bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm"
            data-testid="upgrade-vendor-btn"
          >
            Upgrade to Premium
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="vendor-directory-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold tracking-tight">SC Vendor Directory</h1>
          <p className="text-zinc-400 mt-1">Trusted vendors and service providers for South Carolina nonprofits</p>
        </div>

        {/* Search & Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <form onSubmit={handleSearch} className="flex gap-2 flex-1">
            <div className="relative flex-1">
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                placeholder="Search vendors..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="bg-zinc-900 border-zinc-800 pl-10"
                data-testid="vendor-search-input"
              />
            </div>
            <Button type="submit" className="bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm" data-testid="vendor-search-btn">
              Search
            </Button>
          </form>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="bg-zinc-900 border-zinc-800 w-full sm:w-48" data-testid="vendor-category-filter">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800">
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={county} onValueChange={setCounty}>
            <SelectTrigger className="bg-zinc-900 border-zinc-800 w-full sm:w-48" data-testid="vendor-county-filter">
              <SelectValue placeholder="All Counties" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800">
              <SelectItem value="all">All Counties</SelectItem>
              {counties.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Results count */}
        <p className="text-sm text-zinc-500">{total} vendor{total !== 1 ? "s" : ""} found</p>

        {/* Vendor Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="loading-spinner" />
          </div>
        ) : vendors.length === 0 ? (
          <div className="text-center py-16 text-zinc-500">
            <Storefront className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No vendors match your search</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {vendors.map((vendor) => (
              <Card key={vendor.vendor_id} className="bg-zinc-950 border-zinc-800 hover:border-zinc-700 transition-colors" data-testid={`vendor-card-${vendor.vendor_id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-sm bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                        <Storefront weight="duotone" className="w-5 h-5 text-emerald-400" />
                      </div>
                      <div>
                        <CardTitle className="text-base">{vendor.name}</CardTitle>
                        <span className="text-xs text-emerald-400 font-mono">{vendor.category}</span>
                      </div>
                    </div>
                    {vendor.nonprofit_friendly && (
                      <span className="badge-emerald text-xs px-2 py-0.5 rounded-sm flex items-center gap-1 flex-shrink-0">
                        <CheckCircle weight="fill" className="w-3 h-3" />
                        Verified
                      </span>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-zinc-400 line-clamp-3">{vendor.description}</p>

                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2 text-xs text-zinc-500">
                      <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                      <span>{vendor.city}, SC ({vendor.county} County)</span>
                    </div>
                    {vendor.phone && (
                      <div className="flex items-center gap-2 text-xs text-zinc-500">
                        <Phone className="w-3.5 h-3.5 flex-shrink-0" />
                        <span>{vendor.phone}</span>
                      </div>
                    )}
                  </div>

                  {vendor.website && (
                    <a
                      href={vendor.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs text-emerald-400 hover:text-emerald-300 transition-colors"
                      data-testid={`vendor-website-${vendor.vendor_id}`}
                    >
                      <Globe className="w-3.5 h-3.5" />
                      Visit Website
                    </a>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default VendorDirectory;
